import React, { useState, useEffect, useContext } from "react"
import { DateTime } from "luxon"
import axios from "axios"
import { gql, useQuery } from "@apollo/client"
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"

import auditLogApi from "../../config/auditLogApi"
import { UserContext } from "../../context/userContext"
import { LoaderProgressBar, LoaderMedium } from "../../components/Loaders"
import PageSidebarContent from "../../components/sidebars/PageSidebarContent"
import SidebarDashboardSupervisor from "../../components/sidebars/SidebarDashboardSupervisor"

import AuditLogFilters from "./components/AuditLogFilters"
import LogModal from "./components/LogModal"

import routesDict from "./assets/routesDict.json"

import "../../styles/AppAudit.css"

const QUERY_DROPDOWNS = gql`
  query getOptionsForAuditLogFilters(
    $staff_id: ID!
    $organization_id: ID!
    $enterprise_id: ID!
  ) {
    staffList: staffMembersByOrganization(
      staff_id: $staff_id
      organization_id: $organization_id
    ) {
      staff_id
      staff_name_first
      staff_name_last
    }
    staffListByEnt: getUsersByEnterpriseId(
      staff_id: $staff_id
      enterprise_id: $enterprise_id
    ) {
      staff_id
      staff_name_first
      staff_name_last
    }
    participantList: participants(
      staff_id: $staff_id
      organization_id: $organization_id
    ) {
      participant_id
      participant_name_first
      participant_name_last
    }
    organizationList: organizationsByEnterpriseUid(
      staff_id: $staff_id
      enterprise_id: $enterprise_id
    ) {
      organization_id
      organization_name
    }
  }
`

export default function AuditLog() {
  const {
    current_organization_id,
    session,
    tokens: { getMainToken },
  } = useContext(UserContext)

  const [data, setData] = useState(null)
  const [fetching, setFetching] = useState(true)

  const initialValue = DateTime.now()
  const now = initialValue.toUTC().toJSDate()
  const fifteenDaysAgo = initialValue.minus({ days: 15 }).toUTC().toJSDate()

  const initialFilters = {
    auditType: "participant",
    startDate: fifteenDaysAgo,
    endDate: now,
    participant: "",
    staffMember: "",
    staffFacilitator: "",
    organization_id: current_organization_id,
  }

  const [selectedFilters, setSelectedFilters] = useState(initialFilters)

  const initialOptions = {
    auditType: [
      { label: "Participant", value: "participant", leastRoleAccess: 4 },
      { label: "Staff", value: "staff", leastRoleAccess: 4 },
      { label: "Organization", value: "organization", leastRoleAccess: 3 },
      { label: "Enterprise", value: "enterprise", leastRoleAccess: 2 },
      { label: "System", value: "system", leastRoleAccess: 1 },
    ],
    participant: [],
    staffFacilitator: [],
    staffFacilitatorByEnt: [],
  }

  const [filterOptions, setFilterOptions] = useState(initialOptions)
  const [error, setError] = useState(null)

  const { loading } = useQuery(QUERY_DROPDOWNS, {
    fetchPolicy: "network-only",
    variables: {
      staff_id: session.user.staff_id,
      organization_id: current_organization_id,
      enterprise_id: session.user.enterprise_id,
    },
    skip: !session.user,
    onCompleted: (data) => {
      const {
        staffList = [],
        staffListByEnt = [],
        participantList = [],
        organizationList = [],
      } = data

      const staffOptions = staffList
        .map((staff) => ({
          label: `${staff.staff_name_first} ${staff.staff_name_last}`,
          value: staff.staff_id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))

      const staffOptionsByEnt = staffListByEnt
        .map((staff) => ({
          label: `${staff.staff_name_first} ${staff.staff_name_last}`,
          value: staff.staff_id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))

      const participantOptions = participantList
        .map((participant) => ({
          label: `${participant.participant_name_first} ${participant.participant_name_last}`,
          value: participant.participant_id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))

      const organizationOptions = organizationList
        .map((organization) => ({
          label: organization.organization_name,
          value: organization.organization_id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))

      setFilterOptions((prev) => ({
        ...prev,
        participant: participantOptions,
        staffFacilitator: staffOptions,
        staffFacilitatorByEnt: staffOptionsByEnt,
        organization: organizationOptions,
      }))
    },
  })

  useEffect(() => {
    const handleParams = () => {
      const defaultParams = {
        audit_type: selectedFilters.auditType,
        start_date: DateTime.fromJSDate(selectedFilters.startDate)
          .toUTC()
          .toFormat("yyyy-MM-dd'T'HH:mm:ss'Z'"),
        end_date: DateTime.fromJSDate(selectedFilters.endDate)
          .toUTC()
          .toFormat("yyyy-MM-dd'T'HH:mm:ss'Z'"),
        audit_record_user_id: selectedFilters.staffFacilitator,
      }
      switch (selectedFilters.auditType) {
        case "participant": {
          return {
            ...defaultParams,
            enterprise_id: session.user.enterprise_id,
            organization_id: current_organization_id,
            audit_id: selectedFilters.participant,
          }
        }
        case "staff": {
          return {
            ...defaultParams,
            enterprise_id: session.user.enterprise_id,
            organization_id: current_organization_id,
            audit_id: selectedFilters.staffMember,
          }
        }
        case "organization": {
          return {
            ...defaultParams,
            enterprise_id: session.user.enterprise_id,
            organization_id: selectedFilters.organization_id,
          }
        }
        case "enterprise":
        case "system": {
          return {
            ...defaultParams,
            enterprise_id: session.user.enterprise_id,
          }
        }
        default: {
          return {}
        }
      }
    }

    const fetchData = async () => {
      const getAuditLogApiUrl = auditLogApi()
      try {
        setFetching(true)
        const options = {
          method: "GET",
          url: getAuditLogApiUrl,
          params: handleParams(),
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getMainToken()}`,
          },
          withCredentials: true,
        }
        const response = await axios.request(options)
        const entries = response.data?.entries || []
        setData(entries)
      } catch (error) {
        console.log("Error fetching Audit Log service.", error)
        setError(error)
      } finally {
        setFetching(false)
      }
    }
    fetchData()
  }, [selectedFilters, getMainToken])

  if (loading) return <LoaderMedium />
  if (error) return <div>Error: {error.message}</div>

  const handleReset = () => {
    initialFilters.auditType = selectedFilters.auditType
    setSelectedFilters(initialFilters)
  }

  const handleOnChange = (e) => {
    const { id, value } = e.target
    if (value === null) return
    setSelectedFilters((prev) => ({ ...prev, [id]: value }))
  }

  const handleDataTable = () => {
    if (!data) return null
    if (fetching) return <LoaderProgressBar />

    const dateBody = (rowData) => {
      return DateTime.fromISO(rowData.timestamp).toFormat("yyyy-MM-dd HH:mm")
    }

    const routeBody = (rowData) => {
      const route = rowData.audit_record_type
      if (routesDict[route]) {
        return routesDict[route]
      }
      if (!route.startsWith("/")) {
        return route
      }
      if (route.includes("/form/view/")) {
        function getTemplateId(url) {
          const parts = url.split("/form/view/")
          if (parts.length > 1) {
            return parts[1].split("/")[0] // Extracts the first part after '/form/view/'
          }
          return null
        }
        const templateName = getTemplateId(route)
          .replace(/_/g, " ") // Replace underscores with spaces
          .replace(/\b\w/g, (char) => char.toUpperCase()) // Capitalize the first
        return `Viewed ${templateName} record`
      }
      return route
        .split("/")
        .filter(Boolean) // Remove empty strings from the array
        .pop() // Get the last part of the path
        .replace(/-/g, " ") // Replace hyphens with spaces
        .replace(/_/g, " ") // Replace underscores with spaces
        .replace(/\b\w/g, (char) => char.toUpperCase()) // Capitalize the first
    }

    const handleColumns = () => {
      const columnsPerSelection = () => {
        switch (selectedFilters.auditType) {
          case "participant":
            return [
              {
                field: "audit_name",
                header: "Participant",
              },
              { field: "audit_record_username", header: "Staff facilitator" },
              {
                field: "audit_record_type",
                header: "What was accessed?",
                body: routeBody,
              },
            ]
          case "staff": {
            return [
              {
                field: "audit_name",
                header: "Staff Member",
              },
              { field: "audit_record_username", header: "Staff facilitator" },
              {
                field: "audit_record_type",
                header: "What was accessed?",
                body: routeBody,
              },
            ]
          }
          case "organization":
          case "enterprise":
          case "system": {
            return [
              { field: "audit_name", header: "Subject" },
              {
                field: "audit_record_username",
                header: "Staff facilitator",
              },
              {
                field: "audit_record_type",
                header: "What was accessed?",
                body: routeBody,
              },
            ]
          }
          default:
            return []
        }
      }
      return columnsPerSelection().map((column) => {
        if (column.body) {
          return (
            <Column
              key={column.field}
              field={column.field}
              header={column.header}
              body={column.body}
            />
          )
        }
        return (
          <Column
            key={column.field}
            field={column.field}
            header={column.header}
          />
        )
      })
    }
    return (
      <div className="table-logs">
        <DataTable value={data} stripedRows>
          <Column field="timestamp" header="Date" body={dateBody} />
          {handleColumns()}
          <Column field="audit_title" header="What happened?" />
          <Column
            header="Actions"
            body={(rowData) => <LogModal log={rowData} />}
          />
        </DataTable>
      </div>
    )
  }

  return (
    <>
      <PageSidebarContent>
        <SidebarDashboardSupervisor />
      </PageSidebarContent>
      <div className="app-audit">
        <h1>Audit Log</h1>
        <AuditLogFilters
          selectedFilters={selectedFilters}
          filterOptions={filterOptions}
          handleOnChange={handleOnChange}
          handleReset={handleReset}
        />
        {handleDataTable()}
      </div>
    </>
  )
}
